import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";

interface CountdownTimerProps {
	deadline: string;
	compact?: boolean;
}

function getTimeLeft(deadline: string) {
	const diff = new Date(deadline).getTime() - Date.now();
	if (diff <= 0) return { expired: true, days: 0, hours: 0, minutes: 0, seconds: 0, total: 0 };
	return {
		expired: false,
		days: Math.floor(diff / (1000 * 60 * 60 * 24)),
		hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
		minutes: Math.floor((diff / (1000 * 60)) % 60),
		seconds: Math.floor((diff / 1000) % 60),
		total: diff,
	};
}

export function CountdownTimer({ deadline, compact }: CountdownTimerProps) {
	const [time, setTime] = useState(() => getTimeLeft(deadline));

	useEffect(() => {
		const interval = setInterval(() => setTime(getTimeLeft(deadline)), 1000);
		return () => clearInterval(interval);
	}, [deadline]);

	if (time.expired) {
		return (
			<span className="text-destructive font-semibold text-sm">Deadline passed</span>
		);
	}

	const isUrgent = time.total < 1000 * 60 * 60 * 6; // < 6 hours
	const isWarning = time.total < 1000 * 60 * 60 * 24; // < 24 hours

	if (compact) {
		const parts: string[] = [];
		if (time.days > 0) parts.push(`${time.days}d`);
		parts.push(`${time.hours}h`);
		parts.push(`${time.minutes}m`);
		return (
			<span
				className={cn(
					"font-mono text-sm font-medium tabular-nums",
					isUrgent && "text-destructive",
					isWarning && !isUrgent && "text-amber-600"
				)}
			>
				{parts.join(" ")}
			</span>
		);
	}

	return (
		<div className="flex items-center gap-1.5">
			{time.days > 0 && (
				<TimeBlock value={time.days} label="days" urgent={isUrgent} warning={isWarning} />
			)}
			<TimeBlock value={time.hours} label="hrs" urgent={isUrgent} warning={isWarning} />
			<span className={cn("text-lg font-bold -mx-0.5", isUrgent ? "text-destructive" : "text-muted-foreground")}>:</span>
			<TimeBlock value={time.minutes} label="min" urgent={isUrgent} warning={isWarning} />
			<span className={cn("text-lg font-bold -mx-0.5", isUrgent ? "text-destructive" : "text-muted-foreground")}>:</span>
			<TimeBlock value={time.seconds} label="sec" urgent={isUrgent} warning={isWarning} />
		</div>
	);
}

function TimeBlock({
	value,
	label,
	urgent,
	warning,
}: {
	value: number;
	label: string;
	urgent: boolean;
	warning: boolean;
}) {
	return (
		<div
			className={cn(
				"flex flex-col items-center rounded-lg px-2.5 py-1.5 min-w-[3.25rem]",
				urgent
					? "bg-destructive/10 border border-destructive/20"
					: warning
						? "bg-amber-50 border border-amber-200"
						: "bg-muted border border-border"
			)}
		>
			<span
				className={cn(
					"text-xl font-bold font-mono tabular-nums leading-none",
					urgent ? "text-destructive" : warning ? "text-amber-700" : "text-foreground"
				)}
			>
				{String(value).padStart(2, "0")}
			</span>
			<span className="text-[10px] uppercase tracking-wider text-muted-foreground mt-0.5">
				{label}
			</span>
		</div>
	);
}
